import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Radio, ShieldCheck, Crosshair, Activity } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import TwitchLive from "@/components/TwitchLive";
import Marquee from "@/components/Marquee";
import ProofStats from "@/components/ProofStats";
import Testimonials from "@/components/Testimonials";
import Seo from "@/components/Seo";
import { SERVICES } from "@/content/services";
import { PORTFOLIO_ITEMS, PORTFOLIO_CATEGORIES } from "@/content/portfolio";
import { ENGINES } from "@/content/engines";

const EASE = [0.16, 1, 0.3, 1];
const catLabel = (id) => PORTFOLIO_CATEGORIES.find((c) => c.id === id)?.label || id;
const TEASER_TITLES = ["Tactical Police Officer", "Bank Robbery", "Firetruck", "Fishers Island"];
const TEASER_ITEMS = TEASER_TITLES.map((t) => PORTFOLIO_ITEMS.find((it) => it.title === t)).filter(Boolean);
const TEASER_INTERVAL = 4200;

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Chapter({ number, title, testid, children }) {
  return (
    <section data-testid={testid} className="relative">
      <motion.div
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
      >
        <div className="flex items-end gap-6 mb-10 md:mb-14">
          <span className="ghost-numeral font-sans font-bold text-7xl md:text-9xl leading-[0.8] select-none">{number}</span>
          <div className="pb-1 md:pb-3">
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase mb-2">Chapter {number}</p>
            <h2 className="font-sans font-bold uppercase tracking-tight text-2xl md:text-4xl text-zinc-50">{title}</h2>
          </div>
          <div className="h-px flex-1 bg-white/10 mb-4 hidden md:block" />
        </div>
        {children}
      </motion.div>
    </section>
  );
}

function Hero({ fabCount }) {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 55, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 55, damping: 16 });

  const handleMouse = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <header
      data-testid="home-hero"
      className="relative min-h-[100svh] flex flex-col overflow-hidden border-b border-white/10"
      onMouseMove={handleMouse}
      style={{ perspective: 1200 }}
    >
      <motion.div className="hero-media absolute inset-0 bg-cover bg-center opacity-25" style={{ y: bgY, backgroundImage: "url('https://images.unsplash.com/photo-1605256801685-06e9af2a74f2?auto=format&fit=crop&w=1920&q=60')" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-[#050505]/85 to-[#050505]" />
      <div className="absolute inset-0 grid-texture opacity-60" />

      <motion.div className="relative flex-1 flex flex-col justify-center max-w-7xl mx-auto px-4 sm:px-6 w-full pt-24 pb-16" style={{ opacity: fade }}>
        <motion.p
          data-testid="home-overline"
          className="font-mono text-[10px] sm:text-xs tracking-[0.5em] text-nc-blue uppercase mb-8 md:mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          Signal 001 // Melbourne, Australia
        </motion.p>

        <h1 data-testid="home-title" className="font-sans font-bold uppercase leading-[0.85] tracking-tight text-[13vw] sm:text-[11vw] lg:text-[8.5rem] text-zinc-50">
          <MaskedLine delay={0.35}>Specpixel</MaskedLine>
          <MaskedLine delay={0.5} className="ghost-numeral">Entertainment</MaskedLine>
        </h1>

        <div className="mt-8 md:mt-12 flex flex-col lg:flex-row lg:items-end gap-10">
          <div className="max-w-xl">
            <MaskedLine delay={0.75} className="font-sans font-semibold uppercase tracking-wide text-xl md:text-3xl text-zinc-100">
              Elite co-development &amp; modding — for <span className="text-nc-orange">everyone.</span>
            </MaskedLine>
            <motion.p
              data-testid="home-tagline"
              className="font-mono text-xs sm:text-sm text-zinc-400 mt-5 leading-relaxed"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
            >
              An Australian studio of industry veterans shipping AAA work across Unreal Engine 5, Unity,
              FiveM (GTA V), Arma Reforger and Minecraft. Fixed-price quotes back within 24 hours —
              escrow-protected, identity-verified, NDA-ready from first contact.
            </motion.p>
            <motion.div
              className="mt-8 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            >
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Link to="/contact" data-testid="home-start-cta" className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm px-8 py-4 flex items-center gap-2 transition-colors duration-300" style={{ boxShadow: "0 0 28px rgba(255,107,26,0.35)" }}>
                  Start Your Project <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Link to="/gallery" data-testid="home-hire-cta" className="border border-nc-blue/50 hover:bg-nc-blue/10 text-nc-blue font-sans font-semibold uppercase tracking-[0.2em] text-sm px-8 py-4 transition-colors duration-300">
                  Hire a Specialist
                </Link>
              </motion.div>
              <Link to="/portfolio" data-testid="home-portfolio-cta" className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-zinc-200 transition-colors duration-300 flex items-center gap-1.5">
                or browse the portfolio <ArrowRight className="w-3 h-3" />
              </Link>
            </motion.div>

            {/* Trust strip — engine coverage + assurance signals */}
            <motion.div
              data-testid="hero-trust-strip"
              className="mt-10 max-w-2xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
            >
              <p className="font-mono text-[9px] tracking-[0.35em] text-zinc-600 uppercase mb-3">Full-spectrum engine coverage</p>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(ENGINES).map(([k, v]) => (
                  <span key={k} className={`font-mono text-[9px] tracking-[0.15em] uppercase border px-2.5 py-1.5 flex items-center gap-1.5 ${v.accent}`}>
                    <v.icon className="w-3.5 h-3.5" /> {v.short}
                  </span>
                ))}
              </div>
              <div className="flex gap-x-5 gap-y-1.5 flex-wrap mt-4">
                {["Identity-Verified Personnel", "Escrow-Protected Payments", "AUS Registered Studio"].map((t) => (
                  <span key={t} className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-nc-green" /> {t}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            className="hidden lg:block ml-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.2, ease: EASE }}
          >
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="border border-white/10 bg-black/40 backdrop-blur-md p-8">
              <img src="/assets/newcitizen-logo-blue.png" alt="New Citizen" className="w-64 h-auto" style={{ transform: "translateZ(40px)" }} />
              <p className="font-mono text-[9px] tracking-[0.35em] text-zinc-500 uppercase mt-6" style={{ transform: "translateZ(20px)" }}>
                Flagship IP // V1.0 Alpha → Beta
              </p>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="relative border-t border-white/10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-8 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
            <span data-testid="hero-stat-services">12 Disciplines</span>
            <span data-testid="hero-stat-fab">{fabCount > 0 ? `${fabCount}+ Asset Packs` : "Asset Packs"}</span>
            <span className="hidden sm:inline">2 Shipped Titles</span>
          </div>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
            Scroll <ChevronDown className="w-3.5 h-3.5" />
          </motion.div>
        </div>
      </motion.div>
    </header>
  );
}

function MetricsBar() {
  const stats = [
    ["34+", "Portfolio Pieces Delivered"],
    ["24h", "Quote Turnaround"],
    ["100%", "Escrow-Protected"],
    ["5.0", "Google-Rated Studio"],
  ];
  return (
    <div data-testid="home-metrics-bar" className="border-b border-white/10 bg-black/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4">
        {stats.map(([v, l], i) => (
          <div key={l} data-testid={`metric-${i + 1}`} className={`py-6 md:py-8 px-4 text-center ${i > 0 ? "sm:border-l border-white/10" : ""} ${i >= 2 ? "border-t lg:border-t-0 border-white/10" : ""}`}>
            <p className="font-sans font-bold text-3xl md:text-4xl text-zinc-50">{v}</p>
            <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mt-2">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function VaultTeaser() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused || TEASER_ITEMS.length < 2) return undefined;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % TEASER_ITEMS.length), TEASER_INTERVAL);
    return () => clearInterval(timer.current);
  }, [paused]);

  const current = TEASER_ITEMS[idx];
  if (!current) return null;

  return (
    <div
      data-testid="vault-teaser"
      className="media-caption relative mb-8 md:mb-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Link to="/portfolio" className="group block relative overflow-hidden border border-white/10 hover:border-nc-orange/50 transition-colors duration-500">
        <div className="relative aspect-[16/10] sm:aspect-[21/9] bg-[#0a0a0a] overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={current.src}
              data-testid="vault-teaser-image"
              src={current.src}
              alt={current.title}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center 26%" }}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: EASE }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/40 z-[1]" />
          <div className="absolute inset-0 grid-texture opacity-25 z-[1] pointer-events-none" />

          <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-[2]">
            <span className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase border border-nc-orange/40 bg-black/50 backdrop-blur-sm px-3 py-1.5">
              From the Vault
            </span>
          </div>
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-[2]">
            <span data-testid="vault-teaser-counter" className="font-mono text-[10px] tracking-[0.3em] text-zinc-300 bg-black/50 backdrop-blur-sm px-3 py-1.5">
              {String(idx + 1).padStart(2, "0")} / {String(TEASER_ITEMS.length).padStart(2, "0")}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-[2] p-5 sm:p-8 flex items-end justify-between gap-6">
            <div className="min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.title}
                  initial={{ y: 26, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -18, opacity: 0 }}
                  transition={{ duration: 0.55, ease: EASE }}
                >
                  <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase mb-2">Portfolio // {catLabel(current.category)}</p>
                  <h3 data-testid="vault-teaser-title" className="font-sans font-bold uppercase tracking-tight text-xl sm:text-3xl text-zinc-50 truncate">
                    {current.title}
                  </h3>
                </motion.div>
              </AnimatePresence>
            </div>
            <span className="hidden sm:flex font-mono text-[10px] tracking-[0.3em] text-zinc-300 group-hover:text-nc-orange uppercase items-center gap-2 shrink-0 transition-colors duration-300">
              Open gallery <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </span>
          </div>
        </div>

        <div className="flex bg-black/60 border-t border-white/10">
          {TEASER_ITEMS.map((it, i) => (
            <button
              key={it.src}
              data-testid={`vault-teaser-dot-${i + 1}`}
              onClick={(e) => { e.preventDefault(); setIdx(i); }}
              className="relative flex-1 h-1 bg-white/10 overflow-hidden"
              aria-label={`Show ${it.title}`}
            >
              {i === idx && (
                <motion.span
                  className="absolute inset-y-0 left-0 bg-nc-orange"
                  initial={{ width: "0%" }}
                  animate={{ width: paused ? undefined : "100%" }}
                  transition={{ duration: TEASER_INTERVAL / 1000, ease: "linear" }}
                  style={paused ? { width: "100%" } : undefined}
                />
              )}
              {i !== idx && i < idx && <span className="absolute inset-0 bg-nc-orange/50" />}
            </button>
          ))}
        </div>
      </Link>
    </div>
  );
}

function SpotlightCard({ children, className = "", ...rest }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div onMouseMove={onMove} className={`spotlight-card ${className}`} {...rest}>
      {children}
    </div>
  );
}

function OpsPortalSection() {
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    const load = () => api.get("/activity/global").then((r) => setFeed(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      {/* Global activity feed — command center */}
      <div data-testid="global-activity-feed" className="lg:col-span-7 border border-white/10 bg-black/40 flex flex-col order-2 lg:order-1">
        <div className="border-b border-white/10 px-5 py-3.5 flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 animate-pulse" /> Global Activity Feed
          </p>
          <span className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">Live // Anonymized</span>
        </div>
        <div className="flex-1 overflow-hidden p-5 flex flex-col gap-3">
          {feed.length === 0 && (
            <p className="font-mono text-[10px] text-zinc-600 flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> Awaiting network events — operations appear here as they happen.</p>
          )}
          <AnimatePresence initial={false}>
            {feed.slice(0, 8).map((e) => {
              const eng = ENGINES[e.engine];
              const Icon = eng?.icon || Activity;
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="flex items-center gap-3 border-b border-white/5 pb-3"
                >
                  <span className={`w-7 h-7 border flex items-center justify-center shrink-0 ${eng?.accent || "border-white/15 text-zinc-400"}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] text-zinc-200 truncate">{e.event}</p>
                    <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">{eng?.short || "NETWORK"} // {String(e.at).slice(5, 16).replace("T", " ")} UTC</p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        <div className="border-t border-white/10 px-5 py-3">
          <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-600 uppercase">Personnel identities classified under portal privacy protocol</p>
        </div>
      </div>

      {/* CTAs */}
      <div className="lg:col-span-5 flex flex-col gap-5 order-1 lg:order-2">
        <Link to="/jobs" data-testid="ops-recruit-cta" className="group flex-1 border border-nc-orange/40 bg-nc-orange/5 hover:bg-nc-orange/10 p-8 flex flex-col justify-between transition-colors duration-300">
          <div>
            <p className="font-mono text-[9px] tracking-[0.35em] text-nc-orange uppercase flex items-center gap-2"><Crosshair className="w-4 h-4" /> Clients // Escrow-Protected</p>
            <h3 className="font-sans font-bold uppercase tracking-tight text-3xl text-zinc-50 mt-4 leading-none">Post a Job or<br />Recruit a Specialist</h3>
            <p className="font-mono text-[11px] text-zinc-400 mt-4 leading-relaxed max-w-sm">
              Submit your job to the contractor network and receive pitches — or browse verified specialists directly. Every awarded job becomes an escrow-protected engagement automatically.
            </p>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2 mt-6 group-hover:gap-4 transition-all duration-300">
            Open Job Market <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
        <Link to="/jobs?for=contractors" data-testid="ops-contractor-cta" className="group flex-1 border border-nc-blue/40 bg-nc-blue/5 hover:bg-nc-blue/10 p-8 flex flex-col justify-between transition-colors duration-300">
          <div>
            <p className="font-mono text-[9px] tracking-[0.35em] text-nc-blue uppercase flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Contractors // Get Verified & Pitch</p>
            <h3 className="font-sans font-bold uppercase tracking-tight text-3xl text-zinc-50 mt-4 leading-none">Join as<br />Contractor</h3>
            <p className="font-mono text-[11px] text-zinc-400 mt-4 leading-relaxed max-w-sm">
              Enlist, complete the Contractor Academy and identity verification — then pitch real paid jobs and deploy your services to the gallery or ordnance to the Modding Marketplace.
            </p>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2 mt-6 group-hover:gap-4 transition-all duration-300">
            Browse Open Jobs <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [news, setNews] = useState([]);
  const [fab, setFab] = useState([]);
  const [fabTotal, setFabTotal] = useState(0);

  useEffect(() => {
    api.get("/devlog").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/fab/products").then((r) => {
      setFabTotal(r.data.length);
      const discounted = r.data.filter((p) => p.discount);
      setFab(discounted.length >= 4 ? discounted.slice(0, 4) : r.data.slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Game Development Studio Melbourne — UE5, Unity & Modding Services"
        description="Specpixel Entertainment (Specpixel) — Australian game development studio offering Unreal Engine development services, Unity development, Arma Reforger (Enfusion), FiveM (GTA V) and Minecraft developers for hire. Creators of New Citizen and Living Dead. Outsourcing for everyone."
        path="/"
        keywords="Specpixel, Specpixel Entertainment, game development Melbourne, game outsourcing Australia, UE5 studio, Unreal Engine 5 outsourcing"
      />
      <SiteNav />
      <Hero fabCount={fabTotal} />
      <MetricsBar />
      <ProofStats compact testid="home-proof-stats" />
      {/* Creator Program promo — internal link equity + recruitment */}
      <section data-testid="home-creator-promo" className="border-b border-white/10 bg-nc-blue/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono text-[10px] tracking-[0.35em] text-nc-blue uppercase mb-1.5">New // AI Creator Program</p>
            <p className="font-sans font-bold text-lg md:text-xl text-zinc-50">Make game-ready 3D packs with AI. Get paid bounty + royalty on every sale.</p>
            <p className="font-mono text-[11px] text-zinc-500 mt-1.5">US$20 one-time activation · first approved pack pays it back · free try, no account needed</p>
          </div>
          <Link to="/join-creators" className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 shrink-0">
            Try The Forge Free
          </Link>
        </div>
      </section>
      <Marquee items={["Game Development", "Outsourcing", "Unreal Engine 5", "Motion Capture", "Asset Production", "Cinematics", "Level Design", "Publishing"]} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 flex flex-col gap-24 md:gap-36 w-full">
        <Chapter number="01" title="How It Works" testid="chapter-operations">
          <OpsPortalSection />
        </Chapter>

        <Chapter number="02" title="Worlds We Built" testid="chapter-worlds">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <Link to="/new-citizen" data-testid="chapter-nc" className="lg:col-span-3 block group">
              <SpotlightCard className="border border-nc-blue/30 h-full">
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img src="/assets/media/shot-1.png" alt="New Citizen gameplay" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  <img src="/assets/newcitizen-logo-blue.png" alt="New Citizen" className="absolute bottom-6 left-6 h-14 md:h-20 w-auto" style={{ filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.8))" }} />
                </div>
                <div className="p-6 flex items-center justify-between gap-4 bg-black/40">
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.3em] text-nc-green uppercase mb-2">Out Now — Early Access // V1.0 Alpha → Beta</p>
                    <h3 className="font-sans font-bold uppercase text-xl text-zinc-50 group-hover:text-nc-blue transition-colors duration-300">New Citizen</h3>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-zinc-500 group-hover:text-nc-blue group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
                </div>
              </SpotlightCard>
            </Link>
            <a href="https://store.steampowered.com/app/1084840/Living_Dead/" target="_blank" rel="noopener noreferrer" data-testid="chapter-ld" className="lg:col-span-2 block group">
              <SpotlightCard className="border border-nc-orange/30 h-full flex flex-col">
                <div className="relative overflow-hidden flex-1 min-h-[220px] bg-nc-orange/5 flex items-center justify-center">
                  <div className="absolute inset-0 grid-texture opacity-40" />
                  <span className="ghost-numeral font-sans font-bold text-[9rem] leading-none select-none">LD</span>
                </div>
                <div className="p-6 flex items-center justify-between gap-4 bg-black/40 border-t border-white/10">
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Shipped — PC / Steam</p>
                    <h3 className="font-sans font-bold uppercase text-xl text-zinc-50 group-hover:text-nc-orange transition-colors duration-300">Living Dead</h3>
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-zinc-500 group-hover:text-nc-orange group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
                </div>
              </SpotlightCard>
            </a>
          </div>
        </Chapter>

        <Chapter number="03" title="The Armory" testid="chapter-armory">
          <VaultTeaser />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {fab.map((p, i) => (
              <motion.a
                key={p.id}
                data-testid={`chapter-fab-${i + 1}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
              >
                <SpotlightCard className="border border-white/10 group-hover:border-nc-blue/50 transition-colors duration-300">
                  <div className="relative overflow-hidden aspect-square bg-[#0a0a0a]">
                    <img src={p.thumbnail} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    {p.discount && (
                      <span className="absolute top-3 left-3 bg-nc-orange text-white font-sans font-bold text-xs px-2 py-1 z-[2]">{p.discount}</span>
                    )}
                  </div>
                  <div className="p-4 bg-black/40 border-t border-white/10">
                    <h3 className="font-sans font-semibold uppercase text-xs md:text-sm leading-snug text-zinc-100 group-hover:text-nc-blue transition-colors duration-300 line-clamp-2">{p.name}</h3>
                    <p className="font-mono text-sm text-nc-green mt-2">{p.price}</p>
                  </div>
                </SpotlightCard>
              </motion.a>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <Link to="/asset-packs" data-testid="chapter-armory-link" className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2 hover:gap-4 transition-all duration-300">
              Full catalog — 24 packs <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Chapter>

        <Chapter number="04" title="Disciplines" testid="chapter-disciplines">
          <p data-testid="disciplines-note" className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-6 leading-relaxed">
            Fixed-price milestones // Scoped &amp; quoted within 24 hours // Click any discipline to view the full program
          </p>
          <div className="border-t border-white/10">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4), ease: EASE }}
              >
                <Link
                  to="/services"
                  data-testid={`chapter-service-${s.id}`}
                  className="group flex items-center gap-5 border-b border-white/10 py-4 md:py-5 hover:bg-nc-blue/5 hover:pl-4 transition-all duration-300"
                >
                  <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-600 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-sans font-bold uppercase text-lg md:text-2xl text-zinc-200 group-hover:text-nc-blue transition-colors duration-300 flex-1">{s.title}</span>
                  <span className="hidden md:flex gap-2">
                    {s.engines.map((e) => (
                      <span key={e} className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 border border-white/10 px-2 py-1">{e}</span>
                    ))}
                  </span>
                  <span className="hidden lg:block font-mono text-[9px] tracking-[0.25em] uppercase text-nc-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300 shrink-0">Get a quote</span>
                  <ArrowUpRight className="w-5 h-5 text-zinc-600 group-hover:text-nc-blue group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
                </Link>
              </motion.div>
            ))}
          </div>
        </Chapter>

        <Chapter number="05" title="Client Signals" testid="chapter-testimonials">
          <Testimonials />
        </Chapter>

        <Chapter number="06" title="Transmissions" testid="chapter-transmissions">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {news.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
              >
                <Link to="/news" data-testid={`chapter-news-${i + 1}`} className="block h-full group">
                  <SpotlightCard className="border border-white/10 p-6 h-full group-hover:border-white/30 transition-colors duration-300 bg-black/40">
                    <span className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">{post.category} // {String(post.createdAt).slice(0, 10)}</span>
                    <h3 className="font-sans font-bold uppercase text-lg text-zinc-100 mt-3 leading-snug group-hover:text-nc-blue transition-colors duration-300">{post.title}</h3>
                    <p className="font-mono text-xs text-zinc-500 mt-3 leading-relaxed line-clamp-3">{post.body}</p>
                  </SpotlightCard>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex justify-end">
            <Link to="/news" data-testid="chapter-news-link" className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2 hover:gap-4 transition-all duration-300">
              Open the news hub <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Chapter>

        <section data-testid="home-final-cta" className="relative overflow-hidden border border-nc-orange/30">
          <div className="absolute inset-0 grid-texture opacity-40" />
          <motion.div
            className="relative p-10 md:p-16 flex flex-col items-start gap-8"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Final Transmission</p>
            <h2 className="font-sans font-bold uppercase leading-[0.9] tracking-tight text-4xl md:text-6xl text-zinc-50 max-w-3xl">
              Ready to build? Quote back in <span className="text-nc-orange">24 hours.</span>
            </h2>
            <p className="font-mono text-xs md:text-sm text-zinc-400 max-w-xl leading-relaxed">
              Submit your project details today — a fixed-price, milestone-scoped quote lands in your inbox within 24 hours. We'll handle the work.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Link to="/contact" data-testid="home-quote-cta" className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm px-9 py-4 inline-flex items-center gap-3 transition-colors duration-300" style={{ boxShadow: "0 0 32px rgba(255,107,26,0.35)" }}>
                  Request a Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <Link to="/gallery" data-testid="home-final-hire-cta" className="border border-white/20 hover:border-nc-blue hover:text-nc-blue text-zinc-100 font-sans font-semibold uppercase tracking-[0.2em] text-sm px-9 py-4 inline-flex items-center gap-3 transition-colors duration-300">
                  Hire a Specialist
                </Link>
              </motion.div>
            </div>
            <div className="flex gap-x-5 gap-y-1.5 flex-wrap">
              {["100% Confidential", "NDA Ready", "Escrow-Protected", "Source-Complete Handoff"].map((t) => (
                <span key={t} className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-nc-green" /> {t}
                </span>
              ))}
            </div>
          </motion.div>
        </section>
      </main>

      <TwitchLive />

      <SiteFooter />
    </div>
  );
}
